/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import api from '../../../services/axios';
import { carregarDistribuidores } from '../../../services/dists';
import { FilterBarSemanalTemp } from './FilterBarSemanalTemp';
import { FilterState, PaginatedReportResponse } from './types';
import {
  Container,
  TopHeaderBar,
  HeaderTitleArea,
  HeaderActions,
  DownloadPptxButton,
  KpiGrid,
  KpiCard,
  KpiCardHeader,
  KpiCardBody,
  KpiCardFooter,
  SubTabsRow,
  SubTabButton,
  TableCard,
  TableHeaderBar,
  SearchInputWrapper,
  ResponsiveTable,
  TablePaginationBar,
  StatusPill,
} from './styles';
import {
  FaFilePowerpoint,
  FaChartLine,
  FaWarehouse,
  FaSearch,
  FaLayerGroup,
  FaCalendarWeek,
  FaTable,
  FaExclamationTriangle,
  FaTags,
} from 'react-icons/fa';
import { ThreeDot } from 'react-loading-indicators';
import { toast } from 'react-toastify';
import { formatCurrencyBr } from '../../../constantes';

interface AbaRelatorioSemanalTempProps {
  distribuidores?: any[];
}

export const AbaRelatorioSemanalTemp: React.FC<AbaRelatorioSemanalTempProps> = ({
  distribuidores: propDistribuidores,
}) => {
  const theme = useTheme() as any;
  const isLight = theme?.name === 'light';

  const currentDate = new Date();
  const defaultYear = currentDate.getFullYear();
  const defaultMonth = currentDate.getMonth() + 1;

  const [filters, setFilters] = useState<FilterState>({
    year: defaultYear >= 2026 ? defaultYear : 2026,
    month: defaultMonth,
    week: 4,
    isCustomPeriod: false,
    startDate: `${defaultYear}-${String(defaultMonth).padStart(2, '0')}-01`,
    endDate: `${defaultYear}-${String(defaultMonth).padStart(2, '0')}-22`,
    gidDistributorIds: [],
  });

  const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025, 2024]);
  const [distribuidores, setDistribuidores] = useState<any[]>(propDistribuidores || []);
  const [reportData, setReportData] = useState<PaginatedReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sub-abas analíticas no padrão GID (sem miniaturas de slides)
  const [activeTab, setActiveTab] = useState<'estoque' | 'canais' | 'categorias' | 'dinamica' | 'detalhes'>('estoque');

  // Filtros internos das tabelas
  const [distSearch, setDistSearch] = useState<string>('');
  const [recordsSearch, setRecordsSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 15;

  // Carrega metadados
  useEffect(() => {
    let isMounted = true;
    const fetchMetadata = async () => {
      try {
        const [yearsRes, distsRes] = await Promise.all([
          api.get('/relatorio-temp/years').catch(() => ({ data: { years: [2026, 2025, 2024] } })),
          api.get('/relatorio-temp/distribuidores-gid').catch(() => null),
        ]);

        if (isMounted) {
          if (yearsRes.data?.years && Array.isArray(yearsRes.data.years)) {
            setAvailableYears(yearsRes.data.years);
          }
          if (!propDistribuidores || propDistribuidores.length === 0) {
            const apiDists = distsRes?.data?.distribuidores;
            if (Array.isArray(apiDists) && apiDists.length > 0) {
              setDistribuidores(apiDists);
            } else {
              const fallbackDists = await carregarDistribuidores().catch(() => []);
              if (Array.isArray(fallbackDists) && fallbackDists.length > 0) {
                setDistribuidores(fallbackDists);
              }
            }
          }
        }
      } catch (err) {
        console.warn('Aviso ao carregar metadados do relatório semanal:', err);
      }
    };

    fetchMetadata();
    return () => {
      isMounted = false;
    };
  }, [propDistribuidores]);

  // Consulta do relatório
  const handleFetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setCurrentPage(1);

    try {
      const payload: any = {
        year: filters.year,
        month: filters.month,
        week: filters.week,
        isCustomPeriod: filters.isCustomPeriod,
        gidDistributorIds: filters.gidDistributorIds,
      };

      if (filters.isCustomPeriod) {
        payload.startDate = filters.startDate;
        payload.endDate = filters.endDate;
      }

      const response = await api.post<PaginatedReportResponse>('/relatorio-temp/sellout', payload);
      setReportData(response.data);
      toast.success('Fechamento semanal atualizado com sucesso!');
    } catch (err: any) {
      console.error('Erro ao gerar relatório semanal:', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Falha ao conectar com os bancos de dados ou processar o relatório.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Download da apresentação PPTX
  const handleDownloadPptx = async () => {
    if (!reportData?.pptx?.filename) {
      toast.warning('Gere primeiro o relatório para habilitar o download.');
      return;
    }

    setIsDownloading(true);
    try {
      const response = await api.get(
        `/relatorio-temp/download/${encodeURIComponent(reportData.pptx.filename)}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', reportData.pptx.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Apresentação PowerPoint baixada com sucesso!');
    } catch (err) {
      console.error('Erro ao baixar apresentação PPTX:', err);
      toast.error('Não foi possível realizar o download do arquivo PPTX.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Filtragem da lista de parceiros / estoque
  const filteredEstoque = useMemo(() => {
    const list = reportData?.preview?.estoque?.distribuidoresCriticos || [];
    if (!distSearch.trim()) return list;
    const q = distSearch.toLowerCase().trim();
    return list.filter((d) => d.distribuidor.toLowerCase().includes(q) || d.status.toLowerCase().includes(q));
  }, [reportData?.preview?.estoque?.distribuidoresCriticos, distSearch]);

  // Filtragem e paginação dos registros detalhados
  const filteredRecords = useMemo(() => {
    const list = reportData?.data || [];
    if (!recordsSearch.trim()) return list;
    const q = recordsSearch.toLowerCase().trim();
    return list.filter(
      (r) =>
        r.distribuidor?.toLowerCase().includes(q) ||
        r.cidade?.toLowerCase().includes(q) ||
        r.produto?.toLowerCase().includes(q) ||
        r.operacao?.toLowerCase().includes(q)
    );
  }, [reportData?.data, recordsSearch]);

  const totalRecordPages = Math.ceil(filteredRecords.length / pageSize) || 1;
  const pagedRecords = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, currentPage, pageSize]);

  const kpis = reportData?.preview?.globalKpis;
  const estInfo = reportData?.preview?.estoque;
  const pipeInfo = reportData?.preview?.pipelineSellinSellout;

  return (
    <Container>
      {/* Barra de Título Superior no Padrão GID */}
      <TopHeaderBar>
        <HeaderTitleArea>
          <h1>
            Fechamento Executivo de Sellout & Estoque
            <span className="badge">
              {filters.isCustomPeriod ? 'Período Customizado' : `Semana ${filters.week} / ${filters.month}/${filters.year}`}
            </span>
          </h1>
          <p>
            Consolidação executiva de inteligência de vendas com sincronização em tempo real de Sellout, Metas e Cobertura de Estoque.
          </p>
        </HeaderTitleArea>

        <HeaderActions>
          {reportData && (
            <DownloadPptxButton
              type="button"
              onClick={handleDownloadPptx}
              disabled={isDownloading}
              $isDownloading={isDownloading}
            >
              {isDownloading ? (
                <>
                  <ThreeDot color={isLight ? '#ffffff' : '#001842'} size="small" />
                  <span>Gerando Apresentação...</span>
                </>
              ) : (
                <>
                  <FaFilePowerpoint size={16} />
                  <span>Baixar Apresentação (.pptx)</span>
                </>
              )}
            </DownloadPptxButton>
          )}
        </HeaderActions>
      </TopHeaderBar>

      {/* Card de Filtros no Padrão GID */}
      <FilterBarSemanalTemp
        filters={filters}
        onFilterChange={setFilters}
        availableYears={availableYears}
        distribuidores={distribuidores}
        onApply={handleFetchReport}
        isLoading={isLoading}
      />

      {/* Alerta de Erro */}
      {error && (
        <div
          style={{
            background: isLight ? '#fef2f2' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${isLight ? '#fecaca' : 'rgba(239, 68, 68, 0.35)'}`,
            borderRadius: '14px',
            padding: '14px 18px',
            color: isLight ? '#991b1b' : '#f87171',
            fontSize: '0.82rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <FaExclamationTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Estado Vazio (Antes da primeira busca) */}
      {!isLoading && !reportData && !error && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'linear-gradient(135deg, #00205b 0%, #002b80 100%)',
            border: `1px dashed ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
            borderRadius: '18px',
            padding: '48px 24px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              padding: '16px',
              borderRadius: '50%',
              background: isLight ? 'rgba(2, 132, 199, 0.1)' : 'rgba(166, 193, 11, 0.15)',
              color: isLight ? '#0284c7' : '#a6c10b',
            }}
          >
            <FaChartLine size={32} />
          </div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: isLight ? '#0f172a' : '#ffffff' }}>
            Painel Pronto para Consulta
          </h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.65)', maxWidth: '520px' }}>
            Selecione o período e os distribuidores desejados no painel de filtros acima e clique em <strong>Atualizar Dados</strong> para calcular a posição executiva de vendas e cobertura de estoque.
          </p>
        </div>
      )}

      {/* Conteúdo com Dados Carregados */}
      {!isLoading && reportData && (
        <>
          {/* 1. CARDS DE KPI EXECUTIVOS NO PADRÃO DO GID */}
          <KpiGrid>
            {/* Card 1: Previsão / Fechamento da Semana */}
            <KpiCard $accentColor={isLight ? '#0284c7' : '#a6c10b'}>
              <KpiCardHeader>
                <span className="label">
                  {reportData.preview.isWeekClosed
                    ? `Fechamento Consolidado (S${reportData.preview.effectiveWeek || filters.week})`
                    : `Estimativa Fechamento (S${reportData.preview.effectiveWeek || filters.week})`}
                </span>
                <span
                  className="badge"
                  style={{
                    background: isLight ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166, 193, 11, 0.18)',
                    color: isLight ? '#0284c7' : '#a6c10b',
                  }}
                >
                  {kpis?.deltaEstimadoAxAPerc || 'AxA proj.'}
                </span>
              </KpiCardHeader>
              <KpiCardBody>
                <h2 className="main-val">{kpis?.volumeEstimado || '-'}</h2>
                <span className="sub-val">{kpis?.receitaEstimada ? `Receita: ${kpis.receitaEstimada}` : ''}</span>
              </KpiCardBody>
              <KpiCardFooter>
                <span>Atingimento {reportData.preview.isWeekClosed ? 'Consolidado:' : 'Estimado:'}</span>
                <strong>{kpis?.atingimentoEstimado || '-'} da Meta</strong>
              </KpiCardFooter>
            </KpiCard>

            {/* Card 2: Real Faturado */}
            <KpiCard $accentColor={isLight ? '#10b981' : '#34d399'}>
              <KpiCardHeader>
                <span className="label">Real Faturado (Acumulado)</span>
                <span
                  className="badge"
                  style={{
                    background: isLight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(52, 211, 153, 0.2)',
                    color: isLight ? '#059669' : '#34d399',
                  }}
                >
                  {kpis?.gapAxA || 'AxA real'}
                </span>
              </KpiCardHeader>
              <KpiCardBody>
                <h2 className="main-val">{kpis?.volumeRealizado || '-'}</h2>
                <span className="sub-val">{kpis?.receitaTotal ? `Faturado: ${kpis.receitaTotal}` : ''}</span>
              </KpiCardBody>
              <KpiCardFooter>
                <span>Atingimento Real:</span>
                <strong>{kpis?.atingimentoGeral || '-'}</strong>
              </KpiCardFooter>
            </KpiCard>

            {/* Card 3: Metas de Vendas */}
            <KpiCard $accentColor={isLight ? '#f59e0b' : '#fbbf24'}>
              <KpiCardHeader>
                <span className="label">Metas de Sellout</span>
                <span
                  className="badge"
                  style={{
                    background: isLight ? 'rgba(245, 158, 11, 0.12)' : 'rgba(251, 191, 36, 0.2)',
                    color: isLight ? '#d97706' : '#fbbf24',
                  }}
                >
                  {filters.isCustomPeriod
                    ? `Meta S1..S${reportData.preview.effectiveWeek || filters.week} (Prop.)`
                    : `Meta S1..S${reportData.preview.effectiveWeek || filters.week}`}
                </span>
              </KpiCardHeader>
              <KpiCardBody>
                <h2 className="main-val">{kpis?.metaSemanasAcumuladas || kpis?.metaOrcada || '-'}</h2>
                <span className="sub-val">
                  {(kpis?.metaMesFechado || kpis?.metaOrcada)
                    ? `Mês Fechado: ${kpis.metaMesFechado || kpis.metaOrcada}`
                    : ''}
                </span>
              </KpiCardBody>
              <KpiCardFooter>
                <span>Gap vs Real:</span>
                <strong>{kpis?.gapMeta || '-'}</strong>
              </KpiCardFooter>
            </KpiCard>

            {/* Card 4: Estoque & Cobertura */}
            <KpiCard $accentColor={isLight ? '#8b5cf6' : '#a78bfa'}>
              <KpiCardHeader>
                <span className="label">Estoque da Rede & Giro</span>
                <span
                  className="badge"
                  style={{
                    background: isLight ? 'rgba(139, 92, 246, 0.12)' : 'rgba(167, 139, 250, 0.2)',
                    color: isLight ? '#7c3aed' : '#a78bfa',
                  }}
                >
                  {pipeInfo?.status || 'Ativo'}
                </span>
              </KpiCardHeader>
              <KpiCardBody>
                <h2 className="main-val">{estInfo?.totalEstoque || '-'}</h2>
                <span className="sub-val">Giro Médio da Rede: {estInfo?.diasGiro ? `${estInfo.diasGiro} dias` : '-'}</span>
              </KpiCardBody>
              <KpiCardFooter>
                <span>Saldo Drenagem:</span>
                <strong>{pipeInfo?.drenagemEstoque || '-'}</strong>
              </KpiCardFooter>
            </KpiCard>
          </KpiGrid>

          {/* 2. SUB-ABAS ANALÍTICAS NO PADRÃO DO GID */}
          <SubTabsRow>
            <SubTabButton
              type="button"
              $active={activeTab === 'estoque'}
              onClick={() => setActiveTab('estoque')}
            >
              <FaWarehouse size={13} />
              <span>Cobertura de Estoque por Parceiro</span>
            </SubTabButton>

            <SubTabButton
              type="button"
              $active={activeTab === 'canais'}
              onClick={() => setActiveTab('canais')}
            >
              <FaLayerGroup size={13} />
              <span>Canais, Regionais & Key Account</span>
            </SubTabButton>

            <SubTabButton
              type="button"
              $active={activeTab === 'categorias'}
              onClick={() => setActiveTab('categorias')}
            >
              <FaTags size={13} />
              <span>Categorias & Metas (7 Linhas)</span>
            </SubTabButton>

            <SubTabButton
              type="button"
              $active={activeTab === 'dinamica'}
              onClick={() => setActiveTab('dinamica')}
            >
              <FaCalendarWeek size={13} />
              <span>Dinâmica Semanal (S1..S4)</span>
            </SubTabButton>

            <SubTabButton
              type="button"
              $active={activeTab === 'detalhes'}
              onClick={() => setActiveTab('detalhes')}
            >
              <FaTable size={13} />
              <span>Registros Analíticos de Sellout</span>
            </SubTabButton>
          </SubTabsRow>

          {/* 3. CONTEÚDO DAS SUB-ABAS */}

          {/* ABA 1: COBERTURA DE ESTOQUE POR PARCEIRO */}
          {activeTab === 'estoque' && (
            <TableCard>
              <TableHeaderBar>
                <h3>
                  <FaWarehouse color={isLight ? '#0284c7' : '#a6c10b'} />
                  <span>Posição de Estoque da Rede ({filteredEstoque.length} parceiros)</span>
                </h3>
                <SearchInputWrapper>
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Filtrar parceiro ou status..."
                    value={distSearch}
                    onChange={(e) => setDistSearch(e.target.value)}
                  />
                </SearchInputWrapper>
              </TableHeaderBar>

              <ResponsiveTable>
                <table>
                  <thead>
                    <tr>
                      <th>Distribuidor</th>
                      <th>Estoque Atual (cx)</th>
                      <th>Cobertura (Dias)</th>
                      <th>Classificação de Giro</th>
                      <th>Variação vs Histórico 3M</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEstoque.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '32px' }}>
                          Nenhum distribuidor encontrado com o filtro atual.
                        </td>
                      </tr>
                    ) : (
                      filteredEstoque.map((d, idx) => {
                        let pillType: 'baixo' | 'adequado' | 'excesso' = 'adequado';
                        if (d.dias <= 25) pillType = 'baixo';
                        else if (d.dias > 60) pillType = 'excesso';

                        return (
                          <tr key={idx}>
                            <td style={{ fontWeight: 700 }}>{d.distribuidor}</td>
                            <td style={{ fontWeight: 800 }}>{d.estoque}</td>
                            <td>
                              <strong>{d.dias} dias</strong>
                            </td>
                            <td>
                              <StatusPill $type={pillType}>{d.status}</StatusPill>
                            </td>
                            <td style={{ fontWeight: 600 }}>{d.gap || '-'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </ResponsiveTable>
            </TableCard>
          )}

          {/* ABA 2: CANAIS, REGIONAIS E KEY ACCOUNT */}
          {activeTab === 'canais' && (() => {
            const getAtingColor = (percStr?: string) => {
              if (!percStr) return isLight ? '#64748b' : '#cbd5e1';
              const num = parseFloat(percStr.replace('%', '').replace('+', '').replace(',', '.').trim());
              if (Number.isNaN(num)) return isLight ? '#64748b' : '#cbd5e1';
              return num < 100
                ? (isLight ? '#dc2626' : '#f87171')
                : (isLight ? '#059669' : '#34d399');
            };
            const kaTree = reportData.preview.driverTrees?.keyAccount;

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Canal Indireto */}
                <TableCard>
                  <TableHeaderBar>
                    <h3>
                      <FaLayerGroup color={isLight ? '#0284c7' : '#a6c10b'} />
                      <span>
                        Canal Indireto por Regional (Total: {reportData.preview.canais.indireto.total} • Atingimento: {reportData.preview.canais.indireto.metaPerc})
                      </span>
                    </h3>
                  </TableHeaderBar>
                  <ResponsiveTable>
                    <table>
                      <thead>
                        <tr>
                          <th>Regional / Segmento</th>
                          <th>Volume Sellout</th>
                          <th>Variação MxM</th>
                          <th>Meta Oficial</th>
                          <th>% Atingimento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.preview.canais.indireto.regionais.map((r, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 700 }}>{r.regional}</td>
                            <td style={{ fontWeight: 800 }}>{r.sellout}</td>
                            <td style={{ fontWeight: 600 }}>{r.mxm || r.axa}</td>
                            <td style={{ fontWeight: 700 }}>{r.meta}</td>
                            <td style={{ fontWeight: 800, color: getAtingColor(r.metaPerc) }}>
                              {r.metaPerc}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ResponsiveTable>
                </TableCard>

                {/* Canal Direto */}
                <TableCard>
                  <TableHeaderBar>
                    <h3>
                      <FaLayerGroup color={isLight ? '#0284c7' : '#a6c10b'} />
                      <span>
                        Canal Direto por Praça (Total: {reportData.preview.canais.direto.total} • Atingimento: {reportData.preview.canais.direto.metaPerc})
                      </span>
                    </h3>
                  </TableHeaderBar>
                  <ResponsiveTable>
                    <table>
                      <thead>
                        <tr>
                          <th>Praça / Estado</th>
                          <th>Volume Sellout</th>
                          <th>Variação AxA</th>
                          <th>Meta Oficial</th>
                          <th>% Atingimento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.preview.canais.direto.pracas.map((p, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 700 }}>{p.praca}</td>
                            <td style={{ fontWeight: 800 }}>{p.sellout}</td>
                            <td style={{ fontWeight: 600 }}>{p.axa || p.mxm}</td>
                            <td style={{ fontWeight: 700 }}>{p.meta}</td>
                            <td style={{ fontWeight: 800, color: getAtingColor(p.metaPerc) }}>
                              {p.metaPerc}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ResponsiveTable>
                </TableCard>

                {/* Key Account */}
                {kaTree && (
                  <TableCard>
                    <TableHeaderBar>
                      <h3>
                        <FaLayerGroup color={isLight ? '#0284c7' : '#a6c10b'} />
                        <span>
                          Key Account — Grandes Contas (Total: {kaTree.volumeTotal} • Atingimento: {kaTree.metaPerc})
                        </span>
                      </h3>
                    </TableHeaderBar>
                    <ResponsiveTable>
                      <table>
                        <thead>
                          <tr>
                            <th>Operação / Canal</th>
                            <th>Volume Sellout</th>
                            <th>Variação AxA</th>
                            <th>Meta Oficial</th>
                            <th>% Atingimento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kaTree.nos.map((no, idx) => (
                            <tr key={idx}>
                              <td style={{ fontWeight: 700 }}>{no.segmento}</td>
                              <td style={{ fontWeight: 800 }}>{no.volume}</td>
                              <td style={{ fontWeight: 600 }}>{no.axa || no.mxm}</td>
                              <td style={{ fontWeight: 700 }}>{no.meta}</td>
                              <td style={{ fontWeight: 800, color: getAtingColor(no.metaPerc) }}>
                                {no.metaPerc}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ResponsiveTable>
                  </TableCard>
                )}
              </div>
            );
          })()}

          {/* ABA 2B: CATEGORIAS ESTRATÉGICAS & METAS */}
          {activeTab === 'categorias' && (
            <TableCard>
              <TableHeaderBar>
                <h3>
                  <FaTags color={isLight ? '#0284c7' : '#a6c10b'} />
                  <span>
                    Performance & Metas das 7 Linhas Estratégicas GSA (S1..S{reportData.preview.effectiveWeek || filters.week})
                  </span>
                </h3>
              </TableHeaderBar>
              <ResponsiveTable>
                <table>
                  <thead>
                    <tr>
                      <th>Linha / Categoria Estratégica</th>
                      <th>Sell-Out Real</th>
                      <th>Meta Oficial</th>
                      <th>% Atingimento</th>
                      <th>Variação MxM</th>
                      <th>Share GSA</th>
                      <th>Diagnóstico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.preview.categorias || []).map((cat, idx) => {
                      const atingNum = parseFloat((cat.metaPerc || '0').replace('%', '').replace(',', '.').trim());
                      const isMet = !Number.isNaN(atingNum) && atingNum >= 100;
                      return (
                        <tr key={idx}>
                          <td style={{ fontWeight: 700 }}>{cat.nome}</td>
                          <td style={{ fontWeight: 800 }}>{cat.sellout}</td>
                          <td style={{ fontWeight: 700 }}>{cat.meta}</td>
                          <td>
                            <StatusPill $type={isMet ? 'adequado' : 'baixo'}>
                              {cat.metaPerc}
                            </StatusPill>
                          </td>
                          <td style={{ fontWeight: 600 }}>{cat.mxm || cat.axa}</td>
                          <td style={{ fontWeight: 700 }}>{cat.shareGSA || '-'}</td>
                          <td style={{ fontSize: '0.78rem' }}>{cat.diagnostico}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ResponsiveTable>
            </TableCard>
          )}

          {/* ABA 3: DINÂMICA SEMANAL */}
          {activeTab === 'dinamica' && (
            <TableCard>
              <TableHeaderBar>
                <h3>
                  <FaCalendarWeek color={isLight ? '#0284c7' : '#a6c10b'} />
                  <span>Dinâmica Semanal de Vendas (Evolução S1..S4)</span>
                </h3>
              </TableHeaderBar>
              <ResponsiveTable>
                <table>
                  <thead>
                    <tr>
                      <th>Semana do Ciclo</th>
                      <th>Volume Realizado (cx)</th>
                      <th>Comportamento vs Ciclo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.preview.dinamicaSemanal.semanas.map((s, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 700 }}>{s.semana}</td>
                        <td style={{ fontWeight: 800 }}>{s.volume}</td>
                        <td>
                          <StatusPill $type="info">{s.variacao}</StatusPill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ResponsiveTable>
            </TableCard>
          )}

          {/* ABA 4: REGISTROS ANALÍTICOS PAGINADOS */}
          {activeTab === 'detalhes' && (
            <TableCard>
              <TableHeaderBar>
                <h3>
                  <FaTable color={isLight ? '#0284c7' : '#a6c10b'} />
                  <span>Registros Analíticos de Sellout ({filteredRecords.length} registros)</span>
                </h3>
                <SearchInputWrapper>
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Filtrar por parceiro, produto, cidade..."
                    value={recordsSearch}
                    onChange={(e) => {
                      setRecordsSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </SearchInputWrapper>
              </TableHeaderBar>

              <ResponsiveTable>
                <table>
                  <thead>
                    <tr>
                      <th>Distribuidor</th>
                      <th>Canal / Operação</th>
                      <th>UF / Cidade</th>
                      <th>Produto / SKU</th>
                      <th style={{ textAlign: 'right' }}>Volume (CX)</th>
                      <th style={{ textAlign: 'right' }}>Faturamento (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRecords.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '32px' }}>
                          Nenhum registro encontrado.
                        </td>
                      </tr>
                    ) : (
                      pagedRecords.map((r, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 700 }}>{r.distribuidor}</td>
                          <td>
                            {r.canal} • {r.operacao}
                          </td>
                          <td>
                            {r.estado} - {r.cidade}
                          </td>
                          <td>{r.produto}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800 }}>
                            {Number(r.quantidade_caixas).toLocaleString('pt-BR')} cx
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {formatCurrencyBr(r.valor_total || 0)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </ResponsiveTable>

              {/* Paginação */}
              <TablePaginationBar>
                <span>
                  Exibindo página <strong>{currentPage}</strong> de <strong>{totalRecordPages}</strong> ({filteredRecords.length} itens)
                </span>
                <div className="pagination-controls">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={currentPage >= totalRecordPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalRecordPages, p + 1))}
                  >
                    Próxima
                  </button>
                </div>
              </TablePaginationBar>
            </TableCard>
          )}
        </>
      )}
    </Container>
  );
};

export default AbaRelatorioSemanalTemp;
